import { Alert, Button, TextField } from "@mui/material";
import React, { useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { BUTTON, LABELS, UI_TEXT } from "../../constants";
import { updatePassword } from "../../services/passwordFormService";

const PasswordForm = ({ currentUser }) => {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [passwordsMatchError, setPasswordsMatchError] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});
	const {t} = useTranslation();

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = newPassword === confirmPasswd;
		setValidation({
			...validation,
			confirmpass,
		});
	};

	const isFormValid = () => {
		return (
			validation.minLength &&
			validation.uppercase &&
			validation.specialChar &&
			validation.number &&
			newPassword === confirmPassword
		);
	};

	const handlePasswordSubmit = async (e) => {
		e.preventDefault();
		setSuccessMessage("");
		setErrorMessage("");

		// Check if new password and confirm password match
		if (newPassword !== confirmPassword) {
			setPasswordsMatchError(true);
			return;
		} else {
			setPasswordsMatchError(false);
		}

		const reqBody = {
			userId: currentUser.user_id, // Set userId dynamically based on logged-in user
			current_pass: currentPassword,
			pass: newPassword,
			c_pass: confirmPassword,
		};
		if (!reqBody.current_pass || !reqBody.pass || !reqBody.c_pass) {
			setErrorMessage(t('GENERAL.ALL_FIELDS_ARE_REQUIRED'));
			return;
		}
		if (reqBody.current_pass === reqBody.pass) {
			setErrorMessage("New password can't be the same as your current password.");
			return;
		}
		try {
				const response = await updatePassword(reqBody);
				console.log("Inside passwordform update password response", response);
				
				// Check for success
				if (response.status && response.message === t('GENERAL.PASSWORD_UPDATED_SUCCESSFULLY')) {
					setConfirmPassword("");
					setCurrentPassword("");
					setNewPassword("");
					setErrorMessage("");
					setSuccessMessage(response.message);

					setTimeout(() => {
						setSuccessMessage("");
						setValidation({
							minLength: false,
							uppercase: false,
							specialChar: false,
							number: false,
							confirmPass: false,
						});
					}, 3000);
				} 
				// Check for incorrect current password
				else if (response.status === false && response.message === t('GENERAL.CURRENT_PASSWORD_IS_INCORRECT')) {
					setErrorMessage(response.message);
					setTimeout(() => {
						setErrorMessage("");
					}, 6000);
				} 
				// Handle other error cases
				else if (response.status === false) {
					setErrorMessage(response.error_message || response.message || t('GENERAL.AN_ERROR_OCCURRED'));
					setTimeout(() => {
						setErrorMessage("");
					}, 6000);
				}
			} catch (error) {
				console.error("Error updating password:", error);
				setErrorMessage(t('GENERAL.AN_UNEXPECTED_ERROR_OCCURRED_PLEASE_TRY_AGAIN'));
				setTimeout(() => {
					setErrorMessage("");
				}, 6000);
			}
	};
	return (
		<>
			{successMessage && (
				<Alert severity="success" sx={{ mt: 2, mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mt: 2, mb: 2 }}>
					{errorMessage}
				</Alert>
			)}
			<div className="form-section">
				<TextField
					label={t('LABELS.CURRENT_PASSWORD')}
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={currentPassword}
					onChange={(e) => setCurrentPassword(e.target.value)}
				/>
				<TextField
					label={t('LABELS.NEW_PASSWORD')}
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={newPassword}
					onChange={(e) => {
						const newPassword = e.target.value;
						setNewPassword(newPassword);
						validatePassword(newPassword);
					}}
					error={passwordsMatchError}
					helperText={passwordsMatchError && t('UI_TEXT.PASSWORD_DO_NOT_MATCH')}
				/>
				<TextField
					label={t('LABELS.CONFIRM_NEW_PASSWORD')}
					type="password"
					fullWidth
					size="small"
					className="form-input"
					value={confirmPassword}
					onChange={(e) => {
						setConfirmPassword(e.target.value);
						validateConfirmPassword(e.target.value);
					}}
					error={passwordsMatchError}
					helperText={passwordsMatchError && t('UI_TEXT.PASSWORD_DO_NOT_MATCH')}
				/>
				<div className="password-validation" style={{ marginBottom: "15px" }}>
					<p
						style={{
							fontSize: "11px",
							color: validation.minLength ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.minLength ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						{t('UI_TEXT.MINIMUM_EIGHT_CHARACTERS')}
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.uppercase ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.uppercase ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						{t('UI_TEXT.AT_LEAST_ONE_UPPERCASE')}
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.specialChar ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.specialChar ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						{t('UI_TEXT.AT_LEAST_ONE_SPECIAL_CHARACTERS')}
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.number ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.number ? <CheckCircleIcon fontSize="11px" /> : <ErrorOutlineIcon fontSize="11px" />}{" "}
						{t('UI_TEXT.AT_LEAST_ONE_NUMBER')}
					</p>
					<p
						style={{
							fontSize: "11px",
							color: validation.confirmpass ? "green" : "#aaa",
							marginBottom: 0,
						}}>
						{validation.confirmpass ? (
							<CheckCircleIcon fontSize="11px" />
						) : (
							<ErrorOutlineIcon fontSize="11px" />
						)}{" "}
						{t('UI_TEXT.CONFIRM_PASSWORD_MATCHES')}
					</p>
				</div>
				<Button
					disabled={!isFormValid()}
					variant="contained"
					color="primary"
					className="button-primary"
					onClick={handlePasswordSubmit}>
					{t('BUTTON.UPDATE_PASSWORD')}
				</Button>
			</div>
		</>
	);
};

export default PasswordForm;